import React from "react";
import { useApp } from "@/context/AppContext";
import useSEO from "@/hooks/useSEO";
import { PageHero } from "@/components/primitives";
import { Reveal } from "@/components/Reveal";
import { useCookieConsent } from "@/context/CookieConsent";

const CONTENT = {
  fr: {
    hero_kicker: "Confidentialité",
    hero_title: "Politique de cookies",
    intro:
      "Agrivoire Export SARL attache une grande importance à la protection de votre vie privée. Ce site n'utilise que des cookies strictement nécessaires à son bon fonctionnement. Nous n'employons aucun cookie de suivi, d'analyse d'audience ou de publicité pour les visiteurs.",
    thirdparty_title: "Contenu tiers",
    thirdparty:
      "La page Contact affiche une carte fournie par OpenStreetMap (service tiers). Ce contenu n'est chargé qu'après votre consentement explicite. Lorsqu'il est chargé, votre adresse IP est communiquée à OpenStreetMap afin d'afficher les tuiles cartographiques. OpenStreetMap n'utilise pas de cookies publicitaires.",
    table_title: "Cookies et stockage utilisés",
    cols: ["Nom", "Catégorie", "Finalité", "Durée"],
    rows: [
      ["session_token", "Essentiel (cookie)", "Authentification de l'administrateur — défini uniquement lorsqu'un administrateur se connecte au panneau d'administration. Aucun cookie n'est déposé pour les visiteurs.", "7 jours"],
      ["agrivoire_cookie_consent", "Essentiel (stockage local)", "Mémorise votre choix concernant les cookies afin de ne pas vous le redemander.", "Jusqu'à effacement"],
      ["agrivoire_lang", "Fonctionnel (stockage local)", "Mémorise votre langue préférée (français ou anglais).", "Jusqu'à effacement"],
      ["OpenStreetMap", "Non essentiel (tiers)", "Affiche la carte d'Abidjan sur la page Contact. Chargé uniquement après consentement.", "Session"],
    ],
    manage_title: "Gérer votre choix",
    status_label: "Votre choix actuel :",
    status: { accepted: "Accepté", rejected: "Refusé", null: "Non défini" },
    accept: "Accepter les cookies tiers",
    reject: "Refuser les cookies tiers",
    contact_note: "Pour toute question, contactez-nous à contact@agrivoirexport.com.",
  },
  en: {
    hero_kicker: "Privacy",
    hero_title: "Cookie Policy",
    intro:
      "Agrivoire Export SARL takes your privacy seriously. This website uses only cookies strictly necessary for its proper operation. We do not use any tracking, analytics, or advertising cookies for visitors.",
    thirdparty_title: "Third-party content",
    thirdparty:
      "The Contact page displays a map provided by OpenStreetMap (a third-party service). This content is loaded only after your explicit consent. When loaded, your IP address is shared with OpenStreetMap in order to display the map tiles. OpenStreetMap does not use advertising cookies.",
    table_title: "Cookies and storage used",
    cols: ["Name", "Category", "Purpose", "Duration"],
    rows: [
      ["session_token", "Essential (cookie)", "Administrator authentication — set only when an administrator logs into the admin panel. No cookie is set for visitors.", "7 days"],
      ["agrivoire_cookie_consent", "Essential (local storage)", "Remembers your cookie choice so we don't ask again.", "Until cleared"],
      ["agrivoire_lang", "Functional (local storage)", "Remembers your preferred language (French or English).", "Until cleared"],
      ["OpenStreetMap", "Non-essential (third-party)", "Displays the Abidjan map on the Contact page. Loaded only after consent.", "Session"],
    ],
    manage_title: "Manage your choice",
    status_label: "Your current choice:",
    status: { accepted: "Accepted", rejected: "Rejected", null: "Not set" },
    accept: "Accept third-party cookies",
    reject: "Reject third-party cookies",
    contact_note: "For any question, contact us at contact@agrivoirexport.com.",
  },
};

export default function CookiePolicy() {
  const { lang } = useApp();
  const { consent, accept, reject } = useCookieConsent();
  const c = CONTENT[lang] || CONTENT.fr;
  useSEO(
    { fr: "Politique de cookies | Agrivoire Export SARL", en: "Cookie Policy | Agrivoire Export SARL" },
    { fr: "Ce site n'utilise que des cookies essentiels. Aucun cookie de suivi ou publicitaire.", en: "This site uses only essential cookies. No tracking or advertising cookies." }
  );
  const statusKey = consent === "accepted" ? "accepted" : consent === "rejected" ? "rejected" : "null";

  return (
    <div data-testid="cookie-policy-page">
      <PageHero kicker={c.hero_kicker} title={c.hero_title} />

      <section className="mx-auto max-w-[900px] px-5 py-16 md:px-10 md:py-24">
        <Reveal>
          <p className="text-justify font-poppins text-base leading-relaxed text-neutral-700 md:text-lg">{c.intro}</p>
        </Reveal>

        <Reveal>
          <h2 className="mt-12 font-montserrat text-xl font-bold text-neutral-900">{c.table_title}</h2>
          <div className="mt-5 overflow-x-auto rounded-2xl border border-black/5">
            <table className="w-full border-collapse text-left" data-testid="cookie-table">
              <thead>
                <tr className="bg-[#4E7D2C] text-white">
                  {c.cols.map((h) => (
                    <th key={h} className="px-4 py-3 font-poppins text-xs font-bold uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {c.rows.map((r, i) => (
                  <tr key={i} className={i % 2 ? "bg-neutral-50" : "bg-white"}>
                    <td className="px-4 py-3 align-top font-mono text-xs text-neutral-800">{r[0]}</td>
                    <td className="px-4 py-3 align-top font-poppins text-sm text-neutral-700">{r[1]}</td>
                    <td className="px-4 py-3 align-top font-poppins text-sm text-neutral-600">{r[2]}</td>
                    <td className="px-4 py-3 align-top font-poppins text-sm text-neutral-600">{r[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>

        <Reveal>
          <h2 className="mt-12 font-montserrat text-xl font-bold text-neutral-900">{c.thirdparty_title}</h2>
          <p className="mt-4 text-justify font-poppins text-base leading-relaxed text-neutral-700">{c.thirdparty}</p>
        </Reveal>

        <Reveal>
          <div className="mt-12 rounded-2xl border border-black/5 bg-neutral-50 p-6" data-testid="cookie-manage">
            <h2 className="font-montserrat text-xl font-bold text-neutral-900">{c.manage_title}</h2>
            <p className="mt-3 font-poppins text-sm text-neutral-600">
              {c.status_label}{" "}
              <span className="font-semibold text-neutral-900" data-testid="cookie-status">{c.status[statusKey]}</span>
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button onClick={accept} data-testid="cookie-policy-accept" className="rounded-full bg-[#8CC63F] px-5 py-2.5 font-poppins text-sm font-semibold text-[#0A0A0A] transition-transform hover:scale-105">
                {c.accept}
              </button>
              <button onClick={reject} data-testid="cookie-policy-reject" className="rounded-full border border-black/15 px-5 py-2.5 font-poppins text-sm font-semibold text-neutral-700 transition-colors hover:bg-neutral-100">
                {c.reject}
              </button>
            </div>
            <p className="mt-6 font-poppins text-xs text-neutral-400">{c.contact_note}</p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
